import {useEffect} from 'react'
import {useMutation} from '@apollo/client'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {CREATE_POLICY_STATUS, UPDATE_POLICY_STATUS} from '../../../gql/mutations/policyStatusMutations'
import {
  IModalPolicyStatusProps,
  IResCreatePolicyStatus,
  IResUpdatePolicyStatus,
  PolicyStatusInput,
} from '../policyStatusTypes'

const CATEGORY_OPTIONS = ['PENDING', 'CANCELED', 'COMPLETED', 'QUOTED', 'QUOTE_CONVERTED']

const COLOR_OPTIONS = ['primary', 'success', 'warning', 'danger', 'info', 'secondary', 'dark', 'gray']

const validationSchema = Yup.object({
  code: Yup.string()
    .trim()
    .matches(/^[A-Z0-9_-]+$/, 'Use uppercase letters, numbers, underscores, and hyphens')
    .required('Code is required'),
  title: Yup.string().trim().required('Title is required'),
  color: Yup.string().trim().required('Color is required'),
  category: Yup.string().required('Category is required'),
  enabledForAgents: Yup.boolean(),
  systemReserved: Yup.boolean(),
})

const getFirstError = (errors?: {message?: string}[]) => errors?.[0]?.message || 'Something happened'

export const ModalAddEditPolicyStatus: React.FC<IModalPolicyStatusProps> = ({
  isOpen,
  onClose,
  refetch,
  info,
  canManageReserved,
}) => {
  const isEdit = Boolean(info?.code)
  const [createPolicyStatus, createState] = useMutation<IResCreatePolicyStatus>(CREATE_POLICY_STATUS)
  const [updatePolicyStatus, updateState] = useMutation<IResUpdatePolicyStatus>(UPDATE_POLICY_STATUS)
  const loading = createState.loading || updateState.loading

  const formik = useFormik<PolicyStatusInput>({
    initialValues: {
      code: '',
      title: '',
      color: 'primary',
      category: 'PENDING',
      enabledForAgents: true,
      systemReserved: false,
    },
    validationSchema,
    onSubmit: (values) => {
      const policyStatusData = {
        code: values.code.trim().toUpperCase(),
        title: values.title.trim(),
        color: values.color.trim(),
        category: values.category,
        enabledForAgents: values.enabledForAgents,
        systemReserved: values.systemReserved,
      }

      if (isEdit && info?.code) {
        updatePolicyStatus({
          variables: {
            code: info.code,
            policyStatusData,
          },
        })
        return
      }

      createPolicyStatus({
        variables: {
          policyStatusData,
        },
      })
    },
  })

  useEffect(() => {
    if (!info) return
    formik.setValues({
      code: info.code,
      title: info.title,
      color: info.color,
      category: info.category,
      enabledForAgents: info.enabledForAgents,
      systemReserved: info.systemReserved,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info])

  useEffect(() => {
    if (createState.error) toast.error(`Error: ${createState.error.message}`)
  }, [createState.error])

  useEffect(() => {
    if (updateState.error) toast.error(`Error: ${updateState.error.message}`)
  }, [updateState.error])

  useEffect(() => {
    if (!createState.data) return
    if (createState.data.createPolicyStatus?.success) {
      toast.success('Policy status added correctly')
      onClose()
      refetch()
    } else {
      toast.error(`Error: ${getFirstError(createState.data.createPolicyStatus?.errors)}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createState.data])

  useEffect(() => {
    if (!updateState.data) return
    if (updateState.data.updatePolicyStatus?.success) {
      toast.success('Policy status edited correctly')
      onClose()
      refetch()
    } else {
      toast.error(`Error: ${getFirstError(updateState.data.updatePolicyStatus?.errors)}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateState.data])

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit Policy Status' : 'Add Policy Status'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <div className='mb-3'>
            <Form.Label htmlFor='code'>Code</Form.Label>
            <Form.Control
              id='code'
              value={formik.values.code}
              onChange={(e) => formik.setFieldValue('code', e.target.value.toUpperCase())}
              onBlur={formik.handleBlur}
              isInvalid={Boolean(formik.errors.code && formik.touched.code)}
              disabled={isEdit}
              placeholder='PENDING_DOCUMENT'
            />
            {formik.errors.code && formik.touched.code && (
              <Form.Text className='text-danger'>{formik.errors.code}</Form.Text>
            )}
          </div>

          <div className='mb-3'>
            <Form.Label htmlFor='title'>Title</Form.Label>
            <Form.Control
              id='title'
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={Boolean(formik.errors.title && formik.touched.title)}
              placeholder='Pending Document'
            />
            {formik.errors.title && formik.touched.title && (
              <Form.Text className='text-danger'>{formik.errors.title}</Form.Text>
            )}
          </div>

          <div className='mb-3'>
            <Form.Label htmlFor='category'>Category</Form.Label>
            <Form.Select
              id='category'
              value={formik.values.category}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={Boolean(formik.errors.category && formik.touched.category)}
            >
              {CATEGORY_OPTIONS.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className='mb-3'>
            <Form.Label htmlFor='color'>Color</Form.Label>
            <div className='d-flex gap-2'>
              <Form.Select
                value={COLOR_OPTIONS.includes(formik.values.color) ? formik.values.color : ''}
                onChange={(e) => formik.setFieldValue('color', e.target.value)}
                style={{maxWidth: '170px'}}
              >
                <option value=''>Custom</option>
                {COLOR_OPTIONS.map((color) => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
              </Form.Select>
              <Form.Control
                id='color'
                value={formik.values.color}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={Boolean(formik.errors.color && formik.touched.color)}
                placeholder='success or #198754'
              />
            </div>
            {formik.errors.color && formik.touched.color && (
              <Form.Text className='text-danger'>{formik.errors.color}</Form.Text>
            )}
          </div>

          <div className='d-flex flex-column gap-3'>
            <Form.Check
              type='switch'
              id='enabledForAgents'
              name='enabledForAgents'
              label='Enabled for agents'
              checked={formik.values.enabledForAgents}
              onChange={formik.handleChange}
            />
            <Form.Check
              type='switch'
              id='systemReserved'
              name='systemReserved'
              label='System reserved'
              checked={formik.values.systemReserved}
              onChange={formik.handleChange}
              disabled={!canManageReserved}
            />
            {!canManageReserved && (
              <Form.Text className='text-muted'>
                Only Administrador or SuperAdministrador can set reserved statuses.
              </Form.Text>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' variant='primary' disabled={loading}>
            {loading && <Spinner size='sm' animation='border' className='me-2' />}
            Save Changes
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
